import { useMemo } from "react";

import { styled } from "@linaria/react";
import { ImageBrokenIcon } from "@phosphor-icons/react";

import CodeEditor from "@gx/dls/editor/CodeEditor";
import { PropsWithTheme, withTheme } from "@gx/dls/theme";
import { SourceArtifactResource } from "@gx/ts-contracts/services/source/resources/source_artifact_pb";

import ErrorLayout from "@/layouts/ErrorLayout";
import LoadingLayout from "@/layouts/LoadingLayout";

import { SOURCE_ARTIFACT_CONTENT_TYPE_TO_CODE_EDITOR_LANGUAGE_MAP } from "@/pages/sources/components/edit/tabs/artifacts/constants";

const SourceArtifactContentViewerWrapper = withTheme(styled.div<PropsWithTheme>`
  background-color: ${({ theme }) => theme.color.background.tertiary};
  border-radius: 0 0 6px 6px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  & > div {
    flex: 1;
    min-height: 0;
    min-width: 0;
  }
`);

interface SourceArtifactContentViewerProps {
  artifact: SourceArtifactResource;
  content: string | null;
  isLoading: boolean;
  error: Error | null;
}

const SourceArtifactContentViewer = ({
  artifact,
  content,
  isLoading,
  error,
}: SourceArtifactContentViewerProps) => {
  const lang = SOURCE_ARTIFACT_CONTENT_TYPE_TO_CODE_EDITOR_LANGUAGE_MAP[artifact.contentType];

  const renderContent = useMemo(() => {
    if (!content) return "";
    if (lang === "md") {
      return content.replace(/^\s*```(?:markdown)?\n?/, "").replace(/```$/, "");
    }
    return content;
  }, [content, lang]);

  return (
    <SourceArtifactContentViewerWrapper>
      {isLoading ? (
        <LoadingLayout />
      ) : error ? (
        <ErrorLayout
          icon={ImageBrokenIcon}
          header="Failed to load artifact"
          message="The artifact you are looking for does not exist"
        />
      ) : (
        <CodeEditor
          content={renderContent}
          lang={lang}
          borderRadius={"0 0 6px 6px"}
          isReadOnly
          noBorder
        />
      )}
    </SourceArtifactContentViewerWrapper>
  );
};

export default SourceArtifactContentViewer;
